import styled from "@emotion/styled";
import { DragEvent, useRef, useState, useEffect, ReactNode, ComponentType, Ref } from "react";
import { combineRefs, translate, Uploader } from "@kaltura/mediaspace-shared-utils";
import { UploadDropButton } from "./upload-drop-button/UploadDropButton";
import { UploadProgressBar } from "./upload-progress-bar/UploadProgressBar";
import { UploadProcessingIndicator } from "./upload-processing-indicator/UploadProcessingIndicator";
import { UploadErrorIndicator } from "./upload-error-indicator/UploadErrorIndicator";
import { UploadThumbnail, ThumbnailAction } from "./upload-thumbnail/UploadThumbnail";
import { ButtonClickAnalyticsType } from "@kaltura/mediaspace-shared-types";
import { useButtonAnalytics, useEventHandler } from "@kaltura/mediaspace-shared-hooks";
import { Stack } from "@kaltura/mediaspace-shared-styled";
import { Button, Menu, MenuItem, useMenu } from "@kaltura/ds-react-components";
import { Clear24Icon, Upload24Icon } from "@kaltura/ds-react-icons";

export interface BaseUploadData {
    thumbnailUrl: string;
}

// props shared with the container
export interface InPlaceUploadBaseProps<DataT extends BaseUploadData = BaseUploadData> {
    id: string;
    text: string;
    icon?: ReactNode;
    acceptedTypes?: string;
    serviceUrl: string;
    ks: string;
    data?: DataT;
    editButtonLabel?: string;
    editButtonAriaLabel?: string;
    editMenuLabel: string;
    removeMenuLabel?: string;
    width?: string | number;
    height?: string | number;
    outsideButton?: boolean;
    thumbnailPlaceholder?: ReactNode;
    metadataComponent?: ComponentType<{ data: DataT }>;
    previewComponent?: ComponentType<{ data: DataT }>;
    // optional extra actions to show in the thumbnail actions menu
    additionalActions?: ThumbnailAction[];
    buttonRef?: Ref<HTMLButtonElement>;
    onBlur?: () => void;
    onProcessingChange?: (processing: boolean) => void;
}

// props received from the container
export interface InPlaceUploadProps<DataT extends BaseUploadData = BaseUploadData>
    extends InPlaceUploadBaseProps<DataT> {
    onUploadStart: () => void;
    onUploadDone: (tokenId: string) => void;
    hasError?: boolean;
    onRemove?: () => void;
}

const StyledContainer = styled(
    "div",
    { shouldForwardProp: (propName) => propName !== "width" && propName !== "height" }
)<{ width: string | number; height: string | number }>(({ width, height }) => ({
    width,
    height,
    maxWidth: "100%",
}));

const HiddenInput = styled.input`
    visibility: hidden;
    display: none;
`;

/**
 * in-place file upload widget
 *
 * contains upload button, progress bar, and thumbnail - all
 * in the same container.
 *
 * it will fill the container its rendered in.
 */
export const InPlaceUpload = <DataT extends BaseUploadData>(props: InPlaceUploadProps<DataT>) => {
    const {
        id,
        text,
        icon,
        acceptedTypes = "image/jpeg,image/png",
        serviceUrl,
        ks,
        onUploadStart,
        onUploadDone,
        hasError,
        data,
        editButtonLabel,
        editButtonAriaLabel,
        editMenuLabel,
        removeMenuLabel,
        width = "100%",
        height = "100%",
        outsideButton,
        thumbnailPlaceholder,
        metadataComponent: MetadataComponent,
        previewComponent,
        onRemove,
        additionalActions,
        buttonRef,
        onBlur,
        onProcessingChange,
    } = props;

    const fileUploadRef = useRef<HTMLInputElement>(null);
    const sendButtonAnalytics = useButtonAnalytics();

    const [uploading, setUploading] = useState(false);
    const [cancelUpload, setCancelUpload] = useState<() => void>();
    const [total, setTotal] = useState(0);
    const [uploaded, setUploaded] = useState(0);
    const [processingError, setProcessingError] = useState(hasError);
    const [uploadError, setUploadError] = useState(false);
    const [processing, setProcessing] = useState(false);

    // Notify the parent component when starting or finishing processing
    const handleProcessingChange = useEventHandler(onProcessingChange);
    useEffect(() => {
        handleProcessingChange(uploading || processing);
    }, [handleProcessingChange, uploading, processing]);

    /**
     * user started dragging a file to upload, but didn't drop it yet
     */
    const handleDragOver = (e: DragEvent<HTMLDivElement>) => e.preventDefault();

    /**
     * user dropped a file to upload
     */
    const handleDrop = (e: DragEvent<HTMLDivElement>) => {
        e.preventDefault();

        if (!fileUploadRef.current || uploading || processing) {
            return;
        }

        fileUploadRef.current.files = e.dataTransfer.files;
        uploadFile();
    };

    /**
     * user requested to upload a file,
     * show file browser
     */
    const handleUploadClick = () => {
        openFileSelection();
        sendButtonAnalytics("Upload new", ButtonClickAnalyticsType.ADD);
    };

    /**
     * show file browser
     */
    const openFileSelection = () => {
        if (!fileUploadRef || !fileUploadRef.current) {
            return;
        }

        fileUploadRef.current.click();
    };

    /**
     * user selected a file to upload
     */
    const uploadFile = () => {
        const fileUpload = fileUploadRef.current;
        if (!fileUpload) {
            return;
        }

        const file = fileUpload.files?.[0];
        const uploader = new Uploader();
        if (file) {
            const { cancel } = uploader.upload({
                serviceUrl: serviceUrl,
                ks: ks,
                file: file,
                onProgress: handleProgress,
                onError: handleError,
                onUploadDone: handleUploadDone,
            });

            setCancelUpload(() => cancel);
            setUploading(true);
            setUploaded(0);
            setUploadError(false);
            setProcessing(false);
            setProcessingError(false);
            setTotal(file.size);
            onUploadStart();

            // reset the value, so we can retry uploading the same file again
            fileUpload.value = "";
        }
    };

    const handleProgress = (loaded: number, total: number) => {
        setUploaded(loaded);
    };

    const handleError = (error: string) => {
        setCancelUpload(undefined);
        setUploading(false);
        setProcessing(false);
        setUploadError(true);
    };

    // external processing error
    if (hasError && !processingError) {
        setProcessingError(hasError);
        // handle the error - the string is just for compatability with the uploader
        handleError("");
    }

    const handleUploadDone = (tokenId: string) => {
        setCancelUpload(undefined);
        setUploading(false);
        setProcessing(true);
        onUploadDone(tokenId);
    };

    const handleCancelUpload = () => {
        cancelUpload?.();
        setCancelUpload(undefined);
        setUploading(false);
        setProcessing(false);
        sendButtonAnalytics("Cancel upload", ButtonClickAnalyticsType.DELETE);
    };

    /**
     * user opened the menu
     */
    const handleMenuOpen = () => {
        sendButtonAnalytics("Open menu", ButtonClickAnalyticsType.MENU);
    };

    /**
     * user selected to replace the uploaded file,
     * show file browser
     */
    const handleEditClick = () => {
        openFileSelection();
        sendButtonAnalytics("Upload new from menu", ButtonClickAnalyticsType.CREATE);
    };

    /**
     * user selected to remove an uploaded file
     */
    const handleRemove = () => {
        setProcessing(true);
        onRemove?.();

        sendButtonAnalytics("Remove", ButtonClickAnalyticsType.DELETE);
    };

    // stop processing when thumbnail is added or removed
    useEffect(() => {
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setProcessing(false);
    }, [data]);

    const thumbnailActions: ThumbnailAction[] = [
        { label: editMenuLabel, callback: handleEditClick },
        ...(removeMenuLabel && onRemove && data
            ? [
                  {
                      label: removeMenuLabel,
                      callback: handleRemove,
                  },
              ]
            : []),
        ...(additionalActions ?? []),
    ];

    // The main contents of the component - stored in a temporary variable because they could get different wrappers based on the layout
    const uploadThumbnailResult = (
        <>
            {!uploading && !processing && !uploadError && !data && !thumbnailPlaceholder && (
                <UploadDropButton text={text} icon={icon} onClick={handleUploadClick} />
            )}
            {uploading && <UploadProgressBar ariaLabel={text} loaded={uploaded} total={total} />}

            {processing && !uploadError && <UploadProcessingIndicator />}

            {uploadError && <UploadErrorIndicator onClick={handleUploadClick} />}

            {!uploading && !processing && !uploadError && (data || thumbnailPlaceholder) && (
                <UploadThumbnail
                    data={data}
                    thumbnailPlaceholder={thumbnailPlaceholder}
                    previewComponent={previewComponent}
                    buttonLabel={editButtonLabel}
                    buttonAriaLabel={editButtonAriaLabel}
                    actions={outsideButton ? undefined : thumbnailActions}
                    onMenuOpen={handleMenuOpen}
                />
            )}

            <HiddenInput ref={fileUploadRef} type="file" name="upload" accept={acceptedTypes} onChange={uploadFile} />
        </>
    );

    const { menuProps, buttonProps, close } = useMenu({
        onOpen: handleMenuOpen,
        disabled: uploading || processing,
    });

    if (!outsideButton) {
        return (
            <StyledContainer id={id} width={width} height={height} onDragOver={handleDragOver} onDrop={handleDrop}>
                {uploadThumbnailResult}
            </StyledContainer>
        );
    }

    return (
        <Stack id={id} onDragOver={handleDragOver} onDrop={handleDrop} direction={{ xs: "column", sm: "row" }} gap={2}>
            <StyledContainer width={width} height={height}>
                {uploadThumbnailResult}
            </StyledContainer>

            <Stack direction={"column"} gap={1}>
                {MetadataComponent && data && <MetadataComponent data={data} />}

                <div>
                    {uploading && (
                        <Button
                            color={"secondary"}
                            startIcon={<Clear24Icon />}
                            onClick={handleCancelUpload}
                            onBlur={onBlur}
                            ref={buttonRef}
                        >
                            {translate("Cancel upload")}
                        </Button>
                    )}

                    {!uploading && (
                        <>
                            <Button
                                {...buttonProps}
                                color={"secondary"}
                                startIcon={<Upload24Icon />}
                                aria-label={editButtonAriaLabel}
                                onBlur={onBlur}
                                ref={combineRefs([buttonProps.ref, buttonRef])}
                            >
                                {editButtonLabel}
                            </Button>

                            <Menu {...menuProps}>
                                {/* eslint-disable-next-line react-hooks/refs */}
                                {thumbnailActions.map((action, index) => (
                                    <MenuItem
                                        key={index}
                                        onClick={() => {
                                            action.callback();
                                            close();
                                        }}
                                    >
                                        {action.label}
                                    </MenuItem>
                                ))}
                            </Menu>
                        </>
                    )}
                </div>
            </Stack>
        </Stack>
    );
};
